import React, { useState } from 'react';
import Button from '../../../NecttosComp/Button/Button';
import { Module, UserRole } from '../../types/lms.types';

interface ModuleDropdownProps {
  modules: any[];
  selectedModule: string;
  onModuleChange: (moduleId: string) => void;
  userRole: UserRole;
  onAddModule: () => void;
}

const ModuleDropdown: React.FC<ModuleDropdownProps> = ({
  modules,
  selectedModule,
  onModuleChange,
  userRole,
  onAddModule
}) => {
  const [isOpen, setIsOpen] = useState(false);

  // Add an "All Modules" option to the modules list
  const allModule = {
    _id: 'all',  // Unique ID for "All Modules"
    fullModuleName: 'All Modules',
    description: 'All available modules',
  };

  const allModules = [allModule, ...modules]; // Add "All Modules" as the first item

  // Determine the name to display for the selected module
  const selectedModuleName = selectedModule === 'all'
    ? 'All Modules'
    : modules.find(m => m._id === selectedModule)?.fullModuleName || 'Select a module';

  // Handle module selection
  const handleModuleSelect = (moduleId: string) => {
    onModuleChange(moduleId); // Update the selected module
    setIsOpen(false); // Close the dropdown
  };

  return (
    <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 sm:px-6 lg:px-8 py-4">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Module:</span>
          <div className="relative">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <span>{selectedModuleName}</span>
              <svg className={`w-4 h-4 transform transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {isOpen && (
              <div className="absolute top-full left-0 mt-1 w-80 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg z-50">
                <div className="py-1 max-h-64 overflow-y-auto">
                  {allModules.map((module) => (
                    <button
                      key={module._id}
                      onClick={() => handleModuleSelect(module._id)}
                      className={`w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-600 border-b border-gray-100 dark:border-gray-600 last:border-b-0 ${
                        selectedModule === module._id ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300' : 'text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      <div className="font-medium">{module.fullModuleName}</div>
                      {module.description && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{module.description}</div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {(userRole === 'teacher' || userRole === 'admin') && (
          <Button type="save" onClick={onAddModule}>
            + Add Module
          </Button>
        )}
      </div>
    </div>
  );
};

export default ModuleDropdown;
